import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { GLOBAL } from 'src/app/_config/config';
import { DeleteEmbargoedMerchandise, DownloadFile, FileResponse, Rule238, Rule238Create, Rule238Response, Rule238Update, UpdateEmbargoedMerchandise } from 'src/app/interfaces/rule238.interfaces';

/**
 * Servicio que gestiona los registros de la Regla 238 asociados a las
 * operaciones de los barcos, permitiendo consultarlos, crearlos, actualizarlos
 * y eliminarlos, así como administrar la mercancía embargada y sus archivos.
 */
@Injectable({
  providedIn: 'root'
})
export class Rule238Service {

  constructor(
    private http: HttpClient
  ) { }

  /**
   * Obtiene los registros de la Regla 238 de una operación.
   * @param operationNumber - Número de operación.
   * @returns Observable con los registros de la Regla 238.
   */
  get(operationNumber: number): Observable<Rule238Update[]> {
    const url: string = `${GLOBAL.url}${GLOBAL.barco.rule238.get}${operationNumber}`;
    return this.http.get<Rule238Update[]>(url);
  }

  /**
   * Crea un registro de la Regla 238.
   * @param rule238 - Datos del registro a crear.
   * @returns Observable con la respuesta de la creación.
   */
  create(rule238: Rule238Create): Observable<Rule238Response> {
    const url: string = `${GLOBAL.url}${GLOBAL.barco.rule238.create}`;
    return this.http.post<Rule238Response>(url, rule238);
  }

  /**
   * Actualiza un registro de la Regla 238.
   * @param rule238 - Datos del registro a actualizar.
   * @returns Observable con la respuesta de la actualización.
   */
  update(rule238: Rule238Update): Observable<Rule238Response> {
    const url: string = `${GLOBAL.url}${GLOBAL.barco.rule238.update}`;
    return this.http.put<Rule238Response>(url, rule238);
  }

  /**
   * Elimina un registro de la Regla 238.
   * @param id - Identificador del registro a eliminar.
   * @returns Observable con la respuesta de la eliminación.
   */
  delete(id: number): Observable<Rule238Response> {
    const url: string = `${GLOBAL.url}${GLOBAL.barco.rule238.delete}`;
    return this.http.delete<Rule238Response>(url, {
      headers: new HttpHeaders(),
      body: {
        id
      }
    });
  }

  /**
   * Actualiza la mercancía embargada de un registro de la Regla 238.
   * @param embargoed - Datos de la mercancía embargada a actualizar.
   * @returns Observable con la respuesta de la actualización.
   */
  updateEmbargoed(embargoed: UpdateEmbargoedMerchandise): Observable<Rule238Response> {
    const url: string = `${GLOBAL.url}${GLOBAL.barco.rule238.embargoed.update}`;
    return this.http.post<Rule238Response>(url, embargoed);
  }

  /**
   * Elimina mercancía embargada de un registro de la Regla 238.
   * @param Ids - Identificadores de la mercancía embargada a eliminar.
   * @returns Observable con la respuesta de la eliminación.
   */
  deleteEmbargoed(Ids: DeleteEmbargoedMerchandise): Observable<Rule238Response> {
    const url: string = `${GLOBAL.url}${GLOBAL.barco.rule238.embargoed.delete}`;
    return this.http.delete<Rule238Response>(url, {
      headers: new HttpHeaders(),
      body: Ids
    });
  }

  /**
   * Descarga el archivo de la mercancía embargada.
   * @param Ids - Datos que identifican el archivo a descargar.
   * @returns Observable con el archivo de la mercancía embargada.
   */
  downloadEmbargoedFile(Ids: DownloadFile): Observable<FileResponse> {
    const url: string = `${GLOBAL.url}${GLOBAL.barco.rule238.embargoed.downloadFile}`;
    return this.http.post<FileResponse>(url, Ids);
  }
}
